import * as React from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import { keyframes } from '@emotion/react';
import { cx } from '@emotion/css';
import { isMobile } from 'react-device-detect';

/**
 * The EmptyNotificationItem component represents the NotificationItem in its loading phase or just when there is no data for them.
 */

export interface EmptyNotificationItemProps {
  /**When we want the item to be focused */
  focusedState?: boolean;
  /**The way the loading time be based on.*/
  variant: 'indeterminate' | 'determinate';
  /**Which animation we want to use */
  animationVariant?: 'fillIn' | 'scanner';
}

const fillInAnimation = keyframes({
  '0%': {
    background: 'none',
    opacity: 0.3
  },
  '20%': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.02) 0%,  rgba(255, 255, 255, 0.02) 100%),  rgba(255, 255, 255, 0.005)`
  },
  '40%': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%,  rgba(255, 255, 255, 0.05) 100%),  rgba(255, 255, 255, 0.015)`,
    opacity: 0.6
  },

  '50%': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.08) 0%,  rgba(255, 255, 255, 0.08) 100%),  rgba(255, 255, 255, 0.02)`,
    opacity: 1
  },
  '60%': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%,  rgba(255, 255, 255, 0.05) 100%),  rgba(255, 255, 255, 0.015)`,
    opacity: 0.6
  },
  '80%': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.02) 0%,  rgba(255, 255, 255, 0.02) 100%),  rgba(255, 255, 255, 0.005)`,
    opacity: 0.4
  },
  '100%': {
    background: 'none',
    opacity: 0.3
  }
});

const scannerAnimation = keyframes({
  '0%': {
    transform: `translateX(-110px)`
  },
  '100%': {
    transform: `translateX(370px)`
  }
});

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      display: 'flex',
      alignItems: 'center',
      width: isMobile ? '(100% - 20px)' : 352,
      padding: theme.spacing[2],
      gap: theme.spacing[2],
      borderRadius: theme.borderRadius.lg,
      border: `1px dashed ${theme.color.line['02']}`,
      background: theme.color.overlay['01']
    },
    focusedStyle: {
      background: theme.color.overlay.focused
    },
    childStyle: {
      borderRadius: theme.borderRadius.full,
      border: `1px dashed ${theme.color.line['02']}`,
      background: theme.color.overlay['01']
    },

    emptyAvatar: {
      width: theme.spacing[6],
      height: theme.spacing[6]
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      gap: theme.spacing[1]
    },
    firstRow: {
      width: theme.spacing[40],
      height: theme.spacing[4]
    },
    secondRow: {
      width: theme.spacing[14],
      height: theme.spacing[4]
    },
    animate: {
      animation: `${fillInAnimation} 3.5s ease-in-out infinite`
    },
    scannerContainer: {
      position: 'relative',
      width: isMobile ? '100%' : 370,
      overflow: 'hidden'
    },
    scannerAnimation: {
      position: 'absolute',
      display: 'flex',
      width: '20%',
      height: theme.spacing[14],
      background: `linear-gradient(90deg, rgba(255, 255, 255, 0.005) 0%,  rgba(255, 255, 255, 0.08) 100%), rgba(255, 255, 255, 0.02)`,
      borderRadius: theme.borderRadius.lg,
      animation: `${scannerAnimation} 3s ease-in-out infinite`,
      pointerEvents: 'none'
    }
  };
});

export const EmptyNotificationItem = (props: EmptyNotificationItemProps) => {
  const {
    focusedState = false,
    variant = 'determinate',
    animationVariant = 'scanner'
  } = props;

  const classes = useCSS(props);

  return (
    <div className={`${classes.scannerContainer}`}>
      <div
        className={cx(
          classes.root,
          focusedState ? classes.focusedStyle : '',
          variant === 'indeterminate' && animationVariant === 'fillIn'
            ? classes.animate
            : ''
        )}
      >
        <div className={`${classes.emptyAvatar} ${classes.childStyle}`}></div>
        <div className={`${classes.column}`}>
          <span className={`${classes.firstRow} ${classes.childStyle}`}></span>
          <span className={`${classes.secondRow} ${classes.childStyle}`}></span>
        </div>
        {variant === 'indeterminate' && animationVariant === 'scanner' && (
          <div className={`${classes.scannerAnimation}`}></div>
        )}
      </div>
    </div>
  );
};
